import {
  isPageRefreshing,
  tryAutomaticPageRefresh,
} from '@/lib/safe-page-refresh'
import { isTauri } from '@/lib/utils/tauri'
import { toast } from 'sonner'

export class SignInRefreshRequiredError extends Error {
  constructor() {
    super(
      'Refresh this page to get the latest version of Scrambl, then sign in again.',
    )
    this.name = 'SignInRefreshRequiredError'
  }
}

/** Check at the point of sign-in, including tabs left open between deployments. */
export async function ensureCurrentSignInClient(): Promise<void> {
  const currentVersion = import.meta.env.VITE_APP_VERSION
  if (!import.meta.env.PROD || isTauri() || !currentVersion) return

  let deployedVersion: unknown
  try {
    const response = await fetch('/version.json', {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
      signal: AbortSignal.timeout(3000),
    })
    if (!response.ok) return
    const manifest: unknown = await response.json()
    if (manifest && typeof manifest === 'object' && 'version' in manifest)
      deployedVersion = manifest.version
  } catch {
    // A missing manifest or unavailable update service must not prevent sign-in.
    return
  }

  if (
    typeof deployedVersion === 'string' &&
    deployedVersion.trim() &&
    deployedVersion.trim() !== currentVersion.trim()
  ) {
    tryAutomaticPageRefresh(currentVersion)
    throw new SignInRefreshRequiredError()
  }
}

/** Returns true when the caller should suppress its generic provider error. */
export function showSignInRefreshPrompt(error: unknown): boolean {
  const data =
    error && typeof error === 'object' && 'data' in error ? error.data : null
  if (
    !(error instanceof SignInRefreshRequiredError) &&
    !(
      data &&
      typeof data === 'object' &&
      'code' in data &&
      data.code === 'OAUTH_PROOF_REQUIRED'
    )
  )
    return false

  if (
    !isTauri() &&
    (isPageRefreshing() ||
      tryAutomaticPageRefresh(import.meta.env.VITE_APP_VERSION))
  )
    return true

  if (isTauri()) {
    toast('Update Scrambl to sign in', {
      id: 'sign-in-client-update',
      description:
        'Install the latest version of the desktop app, then try again.',
      duration: Infinity,
    })
  } else {
    toast('Refresh to sign in', {
      id: 'web-version-update',
      description:
        'This page is out of date. Save any unfinished work, then refresh to sign in.',
      duration: Infinity,
      action: {
        label: 'Refresh',
        onClick: () => window.location.reload(),
      },
    })
  }
  return true
}
